import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { partnerErrorMessage } from './partnerError';
import { partnerGateway } from '../services';
import type { PartnerMeResponse } from '../types';
import { PartnerContext } from './PartnerContext';
import { ApiError } from '../../identity/types';

export type PartnerLoad =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; me: PartnerMeResponse };

/**
 * Loads the caller's partner memberships once and decides which screen the portal shows before any partner page: nothing for no membership, an MFA step, the firm's status screen, or the
 * working portal. Only the READY case (a membership on an Active firm, with MFA proven) provides the context the pages use. This is a screen choice, never an access decision: every
 * partner request is authorized again by the server.
 */
export function PartnerProvider({ children, notReady }: { children: ReactNode; notReady: (load: PartnerLoad) => ReactNode }) {
  const [load, setLoad] = useState<PartnerLoad>({ kind: 'loading' });

  const reload = useCallback(async () => {
    try {
      setLoad({ kind: 'ready', me: await partnerGateway.getMe() });
    } catch (error) {
      setLoad({ kind: 'error', message: error instanceof ApiError && error.status === 403 ? 'You do not currently have access to this portal.' : partnerErrorMessage(error, 'The Partner Portal could not be loaded.') });
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the first load on mount, the same pattern SecurityWorkspacePage uses
    void reload();
  }, [reload]);

  const value = useMemo(() => {
    if (load.kind !== 'ready') return null;
    const membership = load.me.memberships.find((m) => m.status === 'Active') ?? load.me.memberships[0];
    if (!membership || !load.me.mfaCompleted) return null;
    return { me: load.me, membership, can: (permission: string) => membership.permissions.includes(permission), reload };
  }, [load, reload]);

  if (!value) {
    return <>{notReady(load)}</>;
  }

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
}
